import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { areSocketTypesCompatible } from '@erp/workflows';

export { areSocketTypesCompatible };

interface DragInfo {
  nodeId: string;
  handleId: string;
  handleType: 'source' | 'target';
  socketType: string; // e.g. 'string', 'entity_reference', 'json', 'any', 'flow', etc.
}

interface ConnectionDragContextType {
  isDragging: boolean;
  dragInfo: DragInfo | null;
  startDrag: (info: DragInfo) => void;
  endDrag: () => void;
  isCompatibleHandle: (
    nodeId: string,
    handleId: string,
    handleType: 'source' | 'target',
    socketType: string,
  ) => boolean;
}

const ConnectionDragContext = createContext<ConnectionDragContextType | null>(
  null,
);

export function ConnectionDragProvider({ children }: { children: ReactNode }) {
  const [dragInfo, setDragInfo] = useState<DragInfo | null>(null);

  const startDrag = useCallback((info: DragInfo) => {
    setDragInfo(info);
  }, []);

  const endDrag = useCallback(() => {
    setDragInfo(null);
  }, []);

  const isCompatibleHandle = useCallback(
    (
      nodeId: string,
      handleId: string,
      handleType: 'source' | 'target',
      socketType: string,
    ) => {
      if (!dragInfo) return true;

      // Can't connect to the same node
      if (dragInfo.nodeId === nodeId) return false;

      // Source can only connect to target, and vice versa
      if (dragInfo.handleType === 'source' && handleType === 'source')
        return false;
      if (dragInfo.handleType === 'target' && handleType === 'target')
        return false;

      // Check socket type compatibility
      // If dragging from source (output), check if target (input) can accept the type
      // If dragging from target (input), check if source (output) can provide the type
      if (dragInfo.handleType === 'source') {
        // Dragging from output to input: source type -> target type
        return areSocketTypesCompatible(dragInfo.socketType, socketType);
      } else {
        // Dragging from input to output: source type -> target type (reversed)
        return areSocketTypesCompatible(socketType, dragInfo.socketType);
      }
    },
    [dragInfo],
  );

  const value = useMemo(
    () => ({
      isDragging: dragInfo !== null,
      dragInfo,
      startDrag,
      endDrag,
      isCompatibleHandle,
    }),
    [dragInfo, startDrag, endDrag, isCompatibleHandle],
  );

  return (
    <ConnectionDragContext.Provider value={value}>
      {children}
    </ConnectionDragContext.Provider>
  );
}

export function useConnectionDrag() {
  const context = useContext(ConnectionDragContext);
  if (!context) {
    // Return a no-op context for read-only mode
    return {
      isDragging: false,
      dragInfo: null,
      startDrag: (_info: DragInfo) => {},
      endDrag: () => {},
      isCompatibleHandle: () => true,
    };
  }
  return context;
}
